import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, LogOut, Download, RefreshCw, Loader2, ArrowLeft, Trash2, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "equilegal_admin_token";

const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 border-blue-200",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  unsubscribed: "bg-slate-100 text-slate-500 border-slate-200",
};

const INTEREST_LABELS = { individual: "Individual/Family", business: "Business", partner: "Partner", website: "Website lead (RMW)", lifeworks: "After-Shift Reset", buyer: "DIY buyer" };

const BIZES = [
  { id: "legal", label: "LegalShield", match: (i) => i !== "website" && i !== "lifeworks" && i !== "buyer", filters: ["all", "individual", "business", "partner"] },
  { id: "rmw", label: "Rocky Mountain Websites", match: (i) => i === "website", filters: ["all", "website"] },
  { id: "lw", label: "LifeWorks", match: (i) => i === "lifeworks" || i === "buyer", filters: ["all", "lifeworks", "buyer"] },
];

const smsHref = (lead) => {
  const digits = (lead.phone || "").replace(/\D/g, "");
  const e164 = digits.length === 11 && digits.startsWith("1") ? `+${digits}` : `+1${digits}`;
  const first = lead.name.trim().split(" ")[0];
  const body = `Hi ${first},

John Here - I got your request for the business overview on Legal Shield.

Click HERE: thrivebeyondservice.com/info#videos

After you watch the overview feel free to reach out to me with any questions. I'm here to help you get started when you're ready.

John
214-500-4192`;
  return `sms:${e164}?&body=${encodeURIComponent(body)}`;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [biz, setBiz] = useState(() => localStorage.getItem("admin_biz") || "legal");

  const fetchLeads = useCallback(async (t, b) => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setLeads(data.leads);
      const s = await axios.get(`${API}/stats?biz=${b || "legal"}`, { headers: { Authorization: `Bearer ${t}` } });
      setStats(s.data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token, biz);
  }, [token, biz, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads([]);
  };

  const setStatus = async (lead, status) => {
    try {
      await axios.patch(`${API}/leads/${lead.id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status.");
    }
  };

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Interest", "Preferred time", "Preferred contact", "Message", "Status", "Submitted"],
      ...filtered.map((l) => [l.name, l.email, l.phone, INTEREST_LABELS[l.interest] || l.interest, l.preferred_time || "", l.preferred_contact === "call" ? "Phone call" : l.preferred_contact === "text" ? "Text" : "", (l.message || "").replace(/\n/g, " "), l.status, new Date(l.created_at).toLocaleString()]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `equilegal-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bizLeads = leads.filter((l) => BIZES.find((b) => b.id === biz)?.match(l.interest));
  const filtered = filter === "all" ? bizLeads : bizLeads.filter((l) => l.interest === filter);
  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[#0B192C] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B192C] grain-overlay relative flex items-center justify-center px-4" data-testid="admin-login-page">
        <div className="absolute inset-0 hero-spotlight" />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative w-full max-w-md bg-white rounded-3xl p-9 shadow-2xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-10 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center"><Scale size={19} /></span>
            <div>
              <p className="font-display font-extrabold text-lg text-[#0B192C] leading-none">John Jones</p>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-blue-600 mt-1">Independent associate portal</p>
            </div>
          </div>
          <h1 className="font-display font-bold text-2xl text-[#0B192C] mb-2">Sign in</h1>
          <p className="text-sm text-slate-500 mb-7">View and manage the leads your page collects.</p>
          <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
            <input data-testid="admin-email-input" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <input data-testid="admin-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
            <button data-testid="admin-login-button" type="submit" disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-3.5 rounded-xl transition-colors duration-300">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
          <Link data-testid="admin-back-link" to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700 transition-colors">
            <ArrowLeft size={14} /> Back to the page
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#1D4ED8] text-white flex items-center justify-center"><Scale size={17} /></span>
            <div>
              <p className="font-display font-extrabold text-[#0B192C] leading-none">Leads</p>
              <p className="text-xs text-slate-500 mt-0.5" data-testid="leads-total-count">{bizLeads.length} total</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-button" onClick={() => fetchLeads(token, biz)} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-blue-700 hover:border-blue-300 transition-colors" title="Refresh">
              <RefreshCw size={15} />
            </button>
            <button data-testid="csv-export-button" onClick={exportCsv} disabled={!filtered.length}
              className="flex items-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="admin-logout-button" onClick={logout} className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:text-red-600 hover:border-red-300 transition-colors" title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="biz-switcher">
          {BIZES.map((b) => (
            <button key={b.id} data-testid={`biz-tab-${b.id}`}
              onClick={() => { setBiz(b.id); localStorage.setItem("admin_biz", b.id); setFilter("all"); }}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                biz === b.id ? "bg-[#0B192C] border-[#0B192C] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-400"
              }`}>
              {b.label} ({leads.filter((l) => b.match(l.interest)).length})
            </button>
          ))}
        </div>
        {stats && (
          <div className="mb-10" data-testid="stats-strip">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Total visits", value: stats.total_views, testid: "stat-total-visits" },
                { label: "Visits, last 7 days", value: stats.week_views, testid: "stat-week-visits" },
                { label: "Leads captured", value: stats.total_leads, testid: "stat-total-leads" },
                { label: "Visit → lead rate", value: `${stats.conversion}%`, testid: "stat-conversion" },
                { label: "Solo ad clicks", value: stats.soload_clicks ?? 0, testid: "stat-soload" },
              ].map((s) => (
                <div key={s.testid} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={s.testid}>
                  <p className="font-display font-extrabold text-3xl text-[#0B192C]">{s.value}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            {biz === "legal" ? (
              <p className="text-xs text-slate-500 mt-4" data-testid="stat-funnel">
                Funnel: <strong>{stats.total_views}</strong> visits → <strong>{stats.engaged ?? 0}</strong> read halfway → <strong>{stats.path_starts ?? 0}</strong> started path → <strong>{stats.form_starts ?? 0}</strong> reached form → <strong>{stats.total_leads}</strong> leads → <strong>{stats.enroll_clicks ?? 0}</strong> clicked enroll → <strong>{stats.more_info_clicks ?? 0}</strong> wanted more info
              </p>
            ) : biz === "lw" ? (
              <p className="text-xs text-slate-500 mt-4" data-testid="stat-funnel">
                Funnel: <strong>{stats.total_views}</strong> visits → <strong>{stats.total_leads}</strong> guide signups
              </p>
            ) : (
              <p className="text-xs text-slate-500 mt-4" data-testid="stat-funnel">
                Funnel: <strong>{stats.total_views}</strong> visits → <strong>{stats.audit_submits ?? 0}</strong> audit requests
              </p>
            )}
            {stats.top_referrers.length > 0 && (
              <p className="text-xs text-slate-500 mt-4" data-testid="stat-referrers">
                Top sources: {stats.top_referrers.map((r) => `${r.source.replace(/^https?:\/\//, "").slice(0, 40)} (${r.count})`).join(" · ")}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-8" data-testid="lead-filters">
          {(BIZES.find((b) => b.id === biz)?.filters || ["all"]).map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                filter === f ? "bg-[#1D4ED8] border-[#1D4ED8] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-400"
              }`}>
              {f === "all" ? `All (${bizLeads.length})` : `${INTEREST_LABELS[f]} (${bizLeads.filter((l) => l.interest === f).length})`}
            </button>
          ))}
        </div>

        {fetching ? (
          <div className="flex justify-center py-24 text-blue-600"><Loader2 size={28} className="animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24" data-testid="leads-empty-state">
            <p className="font-display font-bold text-xl text-[#0B192C] mb-2">No leads yet</p>
            <p className="text-slate-500 text-sm">Share your landing page link — submissions will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-table">
            {filtered.map((lead) => (
              <motion.div key={lead.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-slate-200 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-center"
                data-testid={`lead-row-${lead.id}`}>
                <div className="md:col-span-3">
                  <p className="font-display font-bold text-[#0B192C]" data-testid={`lead-name-${lead.id}`}>{lead.name}</p>
                  <p className="text-xs text-slate-400 mt-1">{new Date(lead.created_at).toLocaleString()}</p>
                </div>
                <div className="md:col-span-3 text-sm text-slate-600">
                  <p>{lead.email}</p>
                  <p className="mt-0.5">{lead.phone || "—"}</p>
                </div>
                <div className="md:col-span-3 text-sm">
                  <span className="inline-block bg-[#EFF6FF] text-blue-700 border border-blue-100 rounded-full px-3 py-1 text-xs font-semibold">
                    {INTEREST_LABELS[lead.interest] || lead.interest}
                  </span>
                  {lead.preferred_time && <p className="text-xs text-slate-500 mt-2">Best time: {lead.preferred_time}</p>}
                  {lead.preferred_contact && <p className="text-xs text-slate-500 mt-1" data-testid={`lead-contact-${lead.id}`}>Prefers: {lead.preferred_contact === "call" ? "Phone call" : "Text"}</p>}
                  {lead.message && <p className="text-xs text-slate-500 mt-1 line-clamp-2">"{lead.message}"</p>}
                </div>
                <div className="md:col-span-3 flex md:justify-end items-center gap-2">
                  {lead.phone && (
                  <a
                    data-testid={`lead-text-${lead.id}`}
                    href={smsHref(lead)}
                    title={`Text ${lead.name} — opens your Messages app with a pre-written message`}
                    className="h-9 px-3.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 flex items-center gap-1.5 text-xs font-semibold hover:bg-sky-100 transition-colors"
                  >
                    <MessageSquareText size={14} /> Text
                  </a>
                  )}
                  <select
                    data-testid={`lead-status-${lead.id}`}
                    value={lead.status}
                    onChange={(e) => setStatus(lead, e.target.value)}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold cursor-pointer focus:outline-none ${STATUS_STYLES[lead.status]}`}
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="closed">Closed</option>
                    <option value="unsubscribed">Unsubscribed</option>
                  </select>
                  <button
                    data-testid={`lead-delete-${lead.id}`}
                    title="Delete lead"
                    onClick={async () => {
                      if (!window.confirm(`Delete ${lead.name}? This can't be undone.`)) return;
                      try {
                        await axios.delete(`${API}/leads/${lead.id}`, { headers: { Authorization: `Bearer ${token}` } });
                        setLeads((ls) => ls.filter((l) => l.id !== lead.id));
                        toast.success("Lead deleted.");
                      } catch {
                        toast.error("Could not delete lead.");
                      }
                    }}
                    className="w-9 h-9 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-600 hover:border-red-300 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
